// ═══════════════════════════════════════════════════════════════════════════════
// API Service - SunuFarmasi Dashboard
// ═══════════════════════════════════════════════════════════════════════════════

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem("token");
}

function authHeaders() {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse(res) {
  if (res.status === 401) {
    // Rediriger seulement si l'utilisateur était déjà connecté (session expirée)
    // Pendant un login, pas de token → on laisse le catch gérer l'erreur
    if (localStorage.getItem("token")) {
      localStorage.clear();
      window.location.href = "/login";
    }
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "Non autorisé");
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    let msg = json.message || `Erreur ${res.status}`;
    // Erreurs de validation : json.data contient { champ: "message précis" }
    if (json.data && typeof json.data === "object" && !Array.isArray(json.data)) {
      const fieldErrors = Object.entries(json.data)
        .map(([field, err]) => `${field} : ${err}`)
        .join(" — ");
      if (fieldErrors) msg = `${msg} (${fieldErrors})`;
    }
    throw new Error(msg);
  }
  return json.data !== undefined ? json.data : json;
}

async function get(endpoint, extraHeaders = {}) {
  const res = await fetch(`${BASE_URL}${endpoint}`, { headers: { ...authHeaders(), ...extraHeaders } });
  return handleResponse(res);
}

async function post(endpoint, body) {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

async function put(endpoint, body, extraHeaders = {}) {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: "PUT",
    headers: { ...authHeaders(), ...extraHeaders },
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

async function del(endpoint) {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handleResponse(res);
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH - Connexion Email/Password (Admin, Pharmacien)
// POST /api/v1/users/auth/login
// ═══════════════════════════════════════════════════════════════════════════════

export const authApi = {
  /**
   * Connexion User (Admin/Pharmacien) → email + password
   */
  login: (email, password) =>
    post("/users/auth/login", { email, password }),

  /**
   * Rafraîchir le token JWT
   */
  refreshToken: (refreshToken) =>
    post("/users/auth/refresh", { refreshToken }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH SYNDICAT - Connexion Username/Password
// POST /api/v1/syndicats/login
// ═══════════════════════════════════════════════════════════════════════════════

export const syndicatAuthApi = {
  /**
   * Connexion Syndicat → username + motDePasse
   * Retourne: { id, code, nom, username, accessToken, refreshToken, ... }
   */
  login: (username, motDePasse) =>
    post("/syndicats/login", { username, motDePasse }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH PHARMACIEN - Connexion email/telephone + motDePasse
// POST /api/v1/pharmaciens/login
// ═══════════════════════════════════════════════════════════════════════════════

export const pharmacienAuthApi = {
  /**
   * Connexion Pharmacien → email + motDePasse
   * Retourne: { token, type, pharmacien }
   */
  login: (email, motDePasse) =>
    post("/pharmaciens/login", { email, motDePasse }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// PHARMACIES
// ═══════════════════════════════════════════════════════════════════════════════

export const pharmacieApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/pharmacies${q ? `?${q}` : ""}`);
  },
  getById: (id) => get(`/pharmacies/${id}`),
  create: (data) => post("/pharmacies", data),
  update: (id, data) => put(`/pharmacies/${id}`, data),
  adminUpdate: (id, data) => put(`/pharmacies/${id}/admin`, data),
  delete: (id) => del(`/pharmacies/${id}`),

  // Pharmacien connecté — ses propres pharmacies (identifié via le JWT côté backend)
  getMine: () => get("/pharmacies/me"),
  updateMine: (id, data) => put(`/pharmacies/${id}`, data),

  // Statut (Syndicat ou Admin)
  activer: (id) => post(`/pharmacies/${id}/activer`),
  suspendre: (id, motif = "Non-paiement") => post(`/pharmacies/${id}/suspendre?motif=${encodeURIComponent(motif)}`),

  // Public
  getByCommune: (communeId) => get(`/public/pharmacies?communeId=${communeId}`),
  getByRegion: (regionId) => get(`/public/pharmacies/region/${regionId}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// PHARMACIENS
// ═══════════════════════════════════════════════════════════════════════════════

export const pharmacienApi = {
  list: () => get("/pharmaciens"),
  getById: (id) => get(`/pharmaciens/${id}`),
  create: (data) => post("/pharmaciens", data),
  update: (id, data) => put(`/pharmaciens/${id}`, data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// EMPLOYÉS
// ═══════════════════════════════════════════════════════════════════════════════

export const employeApi = {
  list: () => get("/employes"),
  getById: (id) => get(`/employes/${id}`),
  create: (data) => post("/employes", data),
  update: (id, data) => put(`/employes/${id}`, data),
  delete: (id) => del(`/employes/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// GARDES (Admin/Pharmacien)
// ═══════════════════════════════════════════════════════════════════════════════

export const gardeApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/gardes${q ? `?${q}` : ""}`);
  },
  listAdmin: () => get("/admin/gardes"),
  getById: (id) => get(`/gardes/${id}`),
  create: (data) => post("/gardes", data),
  update: (id, data) => put(`/gardes/${id}`, data),
  delete: (id) => del(`/gardes/${id}`),
  getSemaine: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/public/gardes/semaine${q ? `?${q}` : ""}`);
  },
  getAujourdHui: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/public/gardes/aujourd-hui${q ? `?${q}` : ""}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// SYNDICATS - Gestion complète (CRUD + Profil + Pharmacies + Gardes)
// ═══════════════════════════════════════════════════════════════════════════════

export const syndicatApi = {
  // Admin: gestion globale
  list: () => get("/syndicats"),
  getById: (id) => get(`/syndicats/${id}`),
  create: (data) => post("/syndicats", data),
  update: (id, data) => put(`/syndicats/${id}`, data),
  suspendre: (id, motif) => post(`/syndicats/${id}/suspendre?motif=${motif || ""}`),
  reactiver: (id) => post(`/syndicats/${id}/reactiver`),

  // Syndicat connecté - profil
  getMe: () => get("/syndicats/me"),
  updateMe: (data) => put("/syndicats/me", data),
  changePassword: (data) => put("/syndicats/me/password", data),

  // Syndicat connecté - pharmacies dans son réseau
  getMyPharmacies: () => get("/syndicats/me/pharmacies"),
  assignerPharmacie: (pharmacieId) => post(`/syndicats/me/pharmacies/${pharmacieId}`),

  // Comptes associés + reset password
  getMyComptes: () => get("/syndicats/me/comptes"),
  resetComptePassword: (compteId, nouveauMotDePasse) =>
    put(`/syndicats/${compteId}/reset-password`, { nouveauMotDePasse }),

  // Plannings de gardes (par syndicatId pour admin, ou /me/ pour connecté)
  getPlannings: (syndicatId) => get(`/syndicats/${syndicatId}/plannings`),
  getPlanningDetail: (planningId) => get(`/plannings/${planningId}`),
  getPlanningActuel: (syndicatId) => get(`/syndicats/${syndicatId}/plannings/actuel`),
  createPlanning: (syndicatId, data) => post(`/syndicats/${syndicatId}/plannings`, data),
  updatePlanning: (planningId, data) => put(`/plannings/${planningId}`, data),
  soumettreForValidation: (planningId) => post(`/plannings/${planningId}/soumettre`),

  // Gardes dans un planning
  addGarde: (planningId, data) => post(`/plannings/${planningId}/gardes`, data),
  addGardesBatch: (planningId, data) => post(`/plannings/${planningId}/gardes/batch`, data),
  validerPlanning: (planningId) => post(`/plannings/${planningId}/valider`),
  publierPlanning: (planningId) => post(`/plannings/${planningId}/publier`),
  annulerGarde: (gardeId, motif) => post(`/gardes/${gardeId}/annuler?motif=${encodeURIComponent(motif)}`),
  deleteGarde: (gardeId) => del(`/gardes/${gardeId}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// STOCK / PRODUITS
// ═══════════════════════════════════════════════════════════════════════════════

export const stockApi = {
  listProduits: () => get("/produits"),
  getProduitById: (id) => get(`/produits/${id}`),
  createProduit: (data) => post("/produits", data),
  updateProduit: (id, data) => put(`/produits/${id}`, data),

  listStock: (pharmacieId) => get(`/stock?pharmacieId=${pharmacieId}`),
  updateStock: (id, data) => put(`/stock/${id}`, data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// VENTES
// ═══════════════════════════════════════════════════════════════════════════════

export const venteApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/ventes${q ? `?${q}` : ""}`);
  },
  getById: (id) => get(`/ventes/${id}`),
  create: (data) => post("/ventes", data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// PATIENTS
// ═══════════════════════════════════════════════════════════════════════════════

export const patientApi = {
  list: () => get("/patients"),
  getById: (id) => get(`/patients/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOCALISATION (public)
// ═══════════════════════════════════════════════════════════════════════════════

export const localisationApi = {
  // Public reads
  getPays:                () => get("/public/pays"),
  getRegions:             () => get("/public/regions"),
  getDepartements:        (regionId) => regionId ? get(`/public/regions/${regionId}/departements`) : get("/public/departements"),
  getCommunes:            (departementId) => departementId ? get(`/public/departements/${departementId}/communes`) : get("/public/communes"),
  getCommunesByRegion:    (regionId) => get(`/public/regions/${regionId}/communes`),

  // Admin CRUD — Pays
  createPays:             (body) => post("/admin/pays", body),
  updatePays:             (id, body) => put(`/admin/pays/${id}`, body),
  deletePays:             (id) => del(`/admin/pays/${id}`),

  // Admin CRUD — Régions
  createRegion:           (body) => post("/admin/regions", body),
  updateRegion:           (id, body) => put(`/admin/regions/${id}`, body),
  deleteRegion:           (id) => del(`/admin/regions/${id}`),

  // Admin CRUD — Départements
  createDepartement:      (body) => post("/admin/departements", body),
  updateDepartement:      (id, body) => put(`/admin/departements/${id}`, body),
  deleteDepartement:      (id) => del(`/admin/departements/${id}`),

  // Admin CRUD — Communes
  createCommune:          (body) => post("/admin/communes", body),
  updateCommune:          (id, body) => put(`/admin/communes/${id}`, body),
  deleteCommune:          (id) => del(`/admin/communes/${id}`),

  // Init Sénégal
  initSenegal:            () => post("/admin/localisation/init-senegal", {}),
};

// ═══════════════════════════════════════════════════════════════════════════════
// MUTUELLES
// ═══════════════════════════════════════════════════════════════════════════════

export const mutuelleApi = {
  list: () => get("/mutuelles"),
  getById: (id) => get(`/mutuelles/${id}`),
  create: (data) => post("/mutuelles", data),
  update: (id, data) => put(`/mutuelles/${id}`, data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ABONNEMENTS
// ═══════════════════════════════════════════════════════════════════════════════

export const subscriptionApi = {
  getPlans: () => get("/subscriptions/plans"),
  list: () => get("/subscriptions"),
};

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT (historique REST)
// ═══════════════════════════════════════════════════════════════════════════════

export const chatApi = {
  getHistory: (roomId) => get(`/chat/${roomId}/history`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// TICKETS SUPPORT (Syndicat → Super Admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const ticketApi = {
  // Syndicat
  create: (data) => post("/tickets", data),
  getMesTickets: () => get("/tickets/me"),

  // Admin
  getAll: () => get("/tickets"),
  getByStatut: (statut) => get(`/tickets/statut/${statut}`),
  getById: (id) => get(`/tickets/${id}`),
  repondre: (id, data) => post(`/tickets/${id}/repondre`, data),
  changerStatut: (id, statut) => {
    const res = fetch(`${BASE_URL}/tickets/${id}/statut?statut=${statut}`, {
      method: "PATCH",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },
  getStats: () => get("/tickets/stats"),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN - Gestion globale (Super Admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const adminApi = {
  // Syndicats
  getSyndicats: () => get("/syndicats"),
  getSyndicatById: (id) => get(`/syndicats/${id}`),
  createSyndicat: (data) => post("/syndicats", data),
  updateSyndicat: (id, data) => put(`/syndicats/${id}`, data),
  suspendreSyndicat: (id, motif) => post(`/syndicats/${id}/suspendre?motif=${encodeURIComponent(motif || "")}`),
  reactiverSyndicat: (id) => post(`/syndicats/${id}/reactiver`),
  enregistrerPaiement: (syndicatId, data) => post(`/syndicats/${syndicatId}/enregistrer-paiement`, data),
  renouvelerAbonnement: (syndicatId, mois = 1) => post(`/syndicats/${syndicatId}/renouveler?mois=${mois}`),
  assignerPharmacieToSyndicat: (syndicatId, pharmacieId) =>
    post(`/syndicats/${syndicatId}/pharmacies/${pharmacieId}/admin`),

  // Patients / Utilisateurs mobiles
  getPatients: () => get("/patients"),
  getPatientById: (id) => get(`/patients/${id}`),
  activerPremiumPatient: (id, mois = 1, reference = "") => put(`/patients/${id}/activer-premium?mois=${mois}${reference ? `&reference=${encodeURIComponent(reference)}` : ""}`),
  suspendrePremiumPatient: (id) => put(`/patients/${id}/suspendre-premium`),
  suspendrePatient: (id) => put(`/patients/${id}/suspendre`),
  reactiverPatient: (id) => put(`/patients/${id}/reactiver`),
  deletePatient: (id) => del(`/patients/${id}`),

  // Syndicats - comptes associés + reset password
  getComptesAssocies: (syndicatId) => get(`/syndicats/${syndicatId}/comptes`),
  resetPasswordSyndicat: (syndicatId, nouveauMotDePasse) =>
    put(`/syndicats/${syndicatId}/reset-password`, { nouveauMotDePasse }),
  getSyndicatPharmacies: (syndicatId) => get(`/syndicats/${syndicatId}/pharmacies`),

  // Zones disponibles : retourne [{id, nom, type}] non encore couverts par un syndicat actif
  // type: "COMMUNE" | "DEPARTEMENT" | "REGION"
  getZonesDisponibles: (type) => get(`/syndicats/zones-disponibles?type=${type}`),

  // Détecte quel syndicat couvre une commune donnée (pour pré-remplir le formulaire pharmacie)
  getSyndicatByCommune: (communeId) => get(`/syndicats/gestionnaire/commune/${communeId}`),

  // Pharmacies
  getPharmacies: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/pharmacies${q ? `?${q}` : ""}`);
  },
  renouvelerAbonnementPharmacie: (id, mois = 12, reference = "") => post(`/pharmacies/${id}/renouveler-abonnement?mois=${mois}${reference ? `&reference=${encodeURIComponent(reference)}` : ""}`),
  relancerPharmacie: (id) => post(`/pharmacies/${id}/relancer-rappel`),
  enregistrerPaiementPharmacie: (id, reference = "") => post(`/pharmacies/${id}/enregistrer-paiement${reference ? `?reference=${encodeURIComponent(reference)}` : ""}`),
  getPharmaciesEnAttente: () => get("/pharmacies/statut/EN_ATTENTE"),
  validerPharmacie: (id, validePar) => {
    const res = fetch(`${BASE_URL}/pharmacies/${id}/valider?validePar=${encodeURIComponent(validePar || "")}`, {
      method: "POST",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },
  rejeterPharmacie: (id, motif) => {
    const res = fetch(`${BASE_URL}/pharmacies/${id}/rejeter?motif=${encodeURIComponent(motif || "")}`, {
      method: "POST",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },
  createPharmacie: (data) => post("/pharmacies/admin", data),
  assignerPharmacieAdmin: (pharmacieId, syndicatId) =>
    fetch(`${BASE_URL}/pharmacies/${pharmacieId}/syndicat/${syndicatId}`, {
      method: "PUT", headers: authHeaders()
    }).then(handleResponse),
  createPharmacien: (data) => post("/pharmaciens/register", data),
  getPharmaciens: () => get("/pharmaciens"),
  updatePharmacien: (id, data) => put(`/pharmaciens/${id}`, data),
  deletePharmacien: (id) => del(`/pharmaciens/${id}/admin`),
  updatePharmacie: (id, data) => put(`/pharmacies/${id}/admin`, data),
  deletePharmacie: (id) => del(`/pharmacies/${id}/admin`),
  deleteSyndicat: (id) => del(`/syndicats/${id}`),
  getAllGardes: () => get("/admin/gardes"),
  getAllPlannings: (params = {}) => {
    const q = Object.entries(params).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    return get(`/admin/plannings${q ? "?" + q : ""}`);
  },

  // Paiements manuels patients
  getPendingPayments: () => get("/payments/pending-validation"),
  validerPaiement: (ref) => put(`/payments/${ref}/validate`, {}),
  rejeterPaiement: (ref, motif) => put(`/payments/${ref}/reject`, { motif }),

  // Tickets support
  getTickets: () => get("/tickets"),
  repondreTicket: (id, data) => post(`/tickets/${id}/repondre`, data),
  getTicketStats: () => get("/tickets/stats"),
  changerStatutTicket: (id, statut) => {
    const res = fetch(`${BASE_URL}/tickets/${id}/statut?statut=${statut}`, {
      method: "PATCH",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },

  // Demandes d'inscription pharmacie (landing page)
  getDemandes: (statut) => {
    const q = statut ? `?statut=${statut}` : "";
    return get(`/admin/demandes-inscription${q}`);
  },
  getDemandeById: (id) => get(`/admin/demandes-inscription/${id}`),
  changerStatutDemande: (id, statut) => {
    const res = fetch(`${BASE_URL}/admin/demandes-inscription/${id}/statut?statut=${statut}`, {
      method: "PATCH",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },
  updateNotesDemande: (id, notes) => {
    const res = fetch(`${BASE_URL}/admin/demandes-inscription/${id}/notes`, {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify({ notes }),
    });
    return res.then(handleResponse);
  },
  supprimerDemande: (id) => {
    const res = fetch(`${BASE_URL}/admin/demandes-inscription/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    return res.then(handleResponse);
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// LIVRAISON MAGAL
// ═══════════════════════════════════════════════════════════════════════════════

export const livraisonApi = {
  // Pharmacien : gérer ses paramètres de livraison
  getSettings: (pharmacieId) => get(`/pharmacies/${pharmacieId}/livraison`),
  updateSettings: (pharmacieId, data) => put(`/pharmacies/${pharmacieId}/livraison`, data),

  // Pharmacien : voir ses demandes
  getMesCommandes: (pharmacieId) => get(`/livraisons/pharmacie/${pharmacieId}`),
  accepter: (id) => put(`/livraisons/${id}/accepter`, {}),
  refuser: (id, motif) => put(`/livraisons/${id}/refuser`, { motif }),

  // Opérateur/Admin : toutes les livraisons
  getAll: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/livraisons${q ? `?${q}` : ""}`);
  },
  getById: (id) => get(`/livraisons/${id}`),

  // Public (app mobile → pèlerin)
  createDemande: (data) => post("/public/livraisons", data),
  getPharmaciesAvecLivraison: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return get(`/public/livraisons/pharmacies${q ? `?${q}` : ""}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILE UPLOAD
// ═══════════════════════════════════════════════════════════════════════════════

export async function uploadTicketFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${BASE_URL}/uploads/ticket`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: formData,
  });
  return handleResponse(res);
}

export default {
  auth: authApi,
  syndicatAuth: syndicatAuthApi,
  pharmacie: pharmacieApi,
  pharmacien: pharmacienApi,
  employe: employeApi,
  garde: gardeApi,
  stock: stockApi,
  vente: venteApi,
  patient: patientApi,
  syndicat: syndicatApi,
  localisation: localisationApi,
  mutuelle: mutuelleApi,
  subscription: subscriptionApi,
  chat: chatApi,
  ticket: ticketApi,
  admin: adminApi,
  livraison: livraisonApi,
};
